import React, { useState } from "react";
import { Form, Button, Container, Row, Col, Alert, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { useSelector, useDispatch } from "react-redux";
import { login, logout, menuPermission } from "../states/slices/authSlice";
import UserData from "../models/UserData";
import { loginUser } from "../services/UserService";
import { loginWithGoogle } from "../services/GoogleAuthService";
import { validateEmail, validatePassword } from "../utils/validation";
import { menuPermissionByUserName } from "../services/PageServie";
import { jwtDecode } from "jwt-decode";

interface LoginProps {
  isTabMode?: boolean;
  onSwitchToRegister?: () => void;
}

function LoginPage({ isTabMode = false, onSwitchToRegister }: LoginProps) {
  const [userData, setUserData] = useState<UserData>({
    username: "",
    email: "",
    password: "",
  });
  const [error, setError] = useState<string | null>(null);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const navigate = useNavigate();

  // Get the authentication state from Redux
  const isAuthenticated = useSelector(
    (state: any) => state.auth.isAuthenticated
  );

  const userSession = useSelector((state: any) => state.auth.userSession);
  const dispatch = useDispatch();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, value } = e.target;
    setUserData({ ...userData, [id]: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation checks
    const emailValidation = validateEmail(userData.email);
    if (!emailValidation.isValid) {
      setError(emailValidation.message!);
      return;
    }

    const passwordValidation = validatePassword(userData.password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.message!);
      return;
    }

    // If all validation passes, you can proceed with login logic
    try {
      const response: any = await loginUser(userData);
      // Dispatch the login action
      const userSession: any = jwtDecode(response.token);
      dispatch(login(response));
      menuPermissionByUserName(userSession.user.username).then(
        (response: any) => {
          dispatch(menuPermission(response));
          // Redirect to dashboard after successful login
          navigate('/dashboard');
        }
      );
      // Clear the form
      setUserData({
        username: "",
        email: "",
        password: "",
      });

      // Clear any previous error
      setError(null);
    } catch (error: any) {
      if (error.response?.status === 401) {
        setError("Invalid email or password. Please try again.");
      } else {
        setError(error.response?.data?.message || "Login failed. Please try again.");
      }
    }
    // Example: You can make an API call to authenticate the user
  };



  const handleGoogleLogin = async () => {
    try {
      setIsGoogleLoading(true);
      setError(null);
      
      const response = await loginWithGoogle();
      const userSession: any = jwtDecode(response.token);
      
      dispatch(login(response));
      
      // Get menu permissions for Google user
      menuPermissionByUserName(userSession.user.username).then(
        (permissionResponse: any) => {
          dispatch(menuPermission(permissionResponse));
          navigate('/dashboard');
        }
      ).catch(() => {
        // If menu permissions fail, still proceed with basic login
        navigate('/dashboard');
      });
      
    } catch (error: any) {
      setError(error.message || 'Google login failed');
    } finally {
      setIsGoogleLoading(false);
    }
  };

  if (isTabMode) {
    return (
      <>
        {isAuthenticated ? (
          <div className="text-center">
            <h4 style={{ color: '#810081' }}>Welcome! You are logged in.</h4>
          </div>
        ) : (
          <>
            <Button
              onClick={handleGoogleLogin}
              disabled={isGoogleLoading}
              className="w-100 py-2 fw-semibold border-2 mb-3"
              variant="outline-danger"
              style={{ borderRadius: '10px' }}
            >
              {isGoogleLoading ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                  Signing in...
                </>
              ) : (
                <>
                  <i className="bi bi-google me-2"></i>
                  Continue with Google
                </>
              )}
            </Button>

            <div className="text-center my-3">
              <span className="text-muted">or</span>
            </div>

            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3" controlId="email">
                <Form.Label className="fw-semibold" style={{ color: '#810081' }}>Email Address</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type="email"
                    placeholder="Enter your email"
                    value={userData.email}
                    onChange={handleChange}
                    required
                    className="py-2 ps-4 border-2"
                    style={{ 
                      borderRadius: '10px', 
                      borderColor: '#FFD600',
                      fontSize: '16px',
                      backgroundColor: '#fefefe'
                    }}
                  />
                  <i className="bi bi-envelope position-absolute" style={{
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#810081'
                  }}></i>
                </div>
              </Form.Group>

              <Form.Group className="mb-4" controlId="password">
                <Form.Label className="fw-semibold" style={{ color: '#810081' }}>Password</Form.Label>
                <div className="position-relative">
                  <Form.Control
                    type="password"
                    placeholder="Enter your password"
                    value={userData.password}
                    onChange={handleChange}
                    required
                    className="py-2 ps-4 border-2"
                    style={{ 
                      borderRadius: '10px', 
                      borderColor: '#FFD600',
                      fontSize: '16px',
                      backgroundColor: '#fefefe'
                    }}
                  />
                  <i className="bi bi-lock position-absolute" style={{
                    left: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#810081'
                  }}></i>
                </div>
              </Form.Group>

              <Button 
                type="submit" 
                className="w-100 py-2 fw-semibold border-0 mb-3"
                style={{
                  backgroundColor: '#FFD600',
                  color: '#810081',
                  borderRadius: '10px',
                  fontSize: '16px'
                }}
              >
                <i className="bi bi-box-arrow-in-right me-2"></i>
                Login
              </Button>

              <div className="text-center">
                <button 
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="btn btn-link text-decoration-none p-0"
                  style={{ color: '#810081' }}
                >
                  Forgot Password?
                </button>
              </div>
            </Form>



            {error && (
              <Alert variant="danger" className="mt-3 border-0" style={{ borderRadius: '10px' }}>
                <i className="bi bi-exclamation-triangle me-2"></i>
                {error}
              </Alert>
            )}
          </>
        )}
      </>
    );
  }

  return (
    <div style={{
      background: 'linear-gradient(135deg, #FFD600 0%, #810081 100%)',
      minHeight: '100vh',
      paddingTop: '2rem',
      paddingBottom: '8rem'
    }}>
      <Container>
        {isAuthenticated ? (
          <div className="text-center text-white">
            <h2>Welcome! You are logged in.</h2>
          </div>
        ) : (
          <Row className="justify-content-center">
            <Col md={6} lg={5}>
              <Card className="shadow-lg border-0" style={{ borderRadius: '15px' }}>
                <Card.Body className="p-4">
                  <div className="text-center mb-4">
                    <div className="mb-3">
                      <i className="bi bi-box-arrow-in-right" style={{ fontSize: '3rem', color: '#810081' }}></i>
                    </div>
                    <h3 className="fw-bold" style={{ color: '#810081' }}>Login To Natakiya</h3>
                  </div>

                  <Button
                    onClick={handleGoogleLogin}
                    disabled={isGoogleLoading}
                    className="w-100 py-2 fw-semibold border-2 mb-3"
                    variant="outline-danger"
                    style={{ borderRadius: '10px' }}
                  >
                    {isGoogleLoading ? (
                      <>
                        <div className="spinner-border spinner-border-sm me-2" role="status"></div>
                        Signing in...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-google me-2"></i>
                        Continue with Google
                      </>
                    )}
                  </Button>

                  <div className="text-center my-3">
                    <span className="text-muted">or</span>
                  </div>

                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3" controlId="email">
                      <Form.Label className="fw-semibold" style={{ color: '#810081' }}>Email Address</Form.Label>
                      <div className="position-relative">
                        <Form.Control
                          type="email"
                          placeholder="Enter your email"
                          value={userData.email}
                          onChange={handleChange}
                          required
                          className="py-2 ps-4 border-2"
                          style={{ 
                            borderRadius: '10px', 
                            borderColor: '#FFD600',
                            fontSize: '16px',
                            backgroundColor: '#fefefe'
                          }}
                        />
                        <i className="bi bi-envelope position-absolute" style={{
                          left: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#810081'
                        }}></i>
                      </div>
                    </Form.Group>

                    <Form.Group className="mb-4" controlId="password">
                      <Form.Label className="fw-semibold" style={{ color: '#810081' }}>Password</Form.Label>
                      <div className="position-relative">
                        <Form.Control
                          type="password"
                          placeholder="Enter your password"
                          value={userData.password}
                          onChange={handleChange}
                          required
                          className="py-2 ps-4 border-2"
                          style={{ 
                            borderRadius: '10px', 
                            borderColor: '#FFD600',
                            fontSize: '16px',
                            backgroundColor: '#fefefe'
                          }}
                        />
                        <i className="bi bi-lock position-absolute" style={{
                          left: '12px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          color: '#810081'
                        }}></i>
                      </div>
                    </Form.Group>

                    <Button 
                      type="submit" 
                      className="w-100 py-2 fw-semibold border-0 mb-3"
                      style={{
                        backgroundColor: '#FFD600',
                        color: '#810081',
                        borderRadius: '10px',
                        fontSize: '16px'
                      }}
                    >
                      <i className="bi bi-box-arrow-in-right me-2"></i>
                      Login
                    </Button>

                    <div className="text-center">
                      <button 
                        type="button"
                        onClick={() => navigate('/forgot-password')}
                        className="btn btn-link text-decoration-none p-0"
                        style={{ color: '#810081' }}
                      >
                        Forgot Password?
                      </button>
                    </div>
                  </Form>

                  {error && (
                    <Alert variant="danger" className="mt-3 border-0" style={{ borderRadius: '10px' }}>
                      <i className="bi bi-exclamation-triangle me-2"></i>
                      {error}
                    </Alert>
                  )}
                 
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default LoginPage;